import { useState } from "react";

import { CloseCircleOutlined } from "@ant-design/icons";
import { Button, FormField, FormLabel } from "@impero-com/ui";
import { RefCallBack } from "react-hook-form";

import { UserOverview } from "api/generated/model";
import { UserTypeahead } from "components/userTypeahead";
import { useUserOverviewQuery } from "components/userTypeahead/hooks/useUserOverviewQuery";

import { InputSkeleton } from "../skeleton/inputSkeleton";

import * as theme from "./theme.css";

type UserId = number;
interface UserTypeheadInputProps extends UserTypeheadInputBaseProps {
    value: UserId | undefined | null;
    onChange: (user: UserId) => void;
    onBlur?: () => void;
    error?: string | undefined;
    fieldRef?: RefCallBack;
    initialData?: UserOverview;
}

export interface UserTypeheadInputBaseProps {
    filter?: (user: UserId) => boolean;
    placeholder?: string;
    label?: string;
    helpText?: string;
    disabled?: boolean;
    allowClear?: boolean;
    onRemove?: () => void;
    removeDisabled?: boolean;
}
export function UserTypeaheadInput({
    error,
    value,
    label,
    helpText,
    disabled,
    filter,
    placeholder,
    onChange,
    onBlur,
    fieldRef,
    initialData,
    allowClear,
    onRemove,
    removeDisabled,
}: UserTypeheadInputProps) {
    const [selectedUserOverview, setSelectedUserOverview] = useState<
        UserOverview | undefined
    >();
    const {
        data,
        error: userQueryError,
        isLoading,
    } = useUserOverviewQuery(value, selectedUserOverview ?? initialData);

    if (value !== undefined && value !== null && !data) {
        return (
            <InputSkeleton
                label={label}
                error={userQueryError?.message}
                enableAnimation={isLoading}
                className={theme.userTypeaheadSkeleton}
            />
        );
    }

    const handleSelect = (user: UserOverview) => {
        onChange(user.id);
        onBlur?.(); // handled here due to blur being called in create dialog - prefer native otherwise
        setSelectedUserOverview(user);
    };

    const inputElement = (
        <UserTypeahead
            fieldRef={fieldRef}
            filter={filter ? (user) => filter(user.id) : undefined}
            placeholder={placeholder}
            onSelect={handleSelect}
            value={data}
            status={error ? "error" : undefined}
            disabled={disabled}
            allowClear={allowClear}
            id="userTypeahead"
        />
    );

    return (
        <FormField error={error} lite={label === undefined}>
            {label && (
                <FormLabel disabled={disabled} helpText={helpText}>
                    {label}
                </FormLabel>
            )}
            {onRemove ? (
                <div className={theme.userTypeaheadWithRemove}>
                    {inputElement}
                    <Button
                        variant="text"
                        icon={<CloseCircleOutlined />}
                        disabled={disabled || !value || removeDisabled}
                        onClick={onRemove}
                    />
                </div>
            ) : (
                inputElement
            )}
        </FormField>
    );
}
